import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { logQuestEvent } from 'features/quest/utils/analytics';
import { StyleSheet } from 'react-native';

import { Box } from 'components/ui';
import { useRoute } from 'hooks/navigation/use-route';
import { routes } from 'navigation/routing';
import { CustomEventNames } from 'state/amplitude/constants';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { spacing } from 'styles/constants';

import { useCmsQuestDetailLazyQuery } from './cms-hooks/use-cms-quest-detail';
import { QuestActivationSource } from './loyalty-quest/quest-activation-source';
import { useActivateQuest } from './loyalty-quest/use-activate-quest';
import { canActivateQuest, isQuestCompleted } from './loyalty-quest/utils';
import { QuestCard } from './quest-card';
import type { IQuestCardWrapperProps } from './types';
import { useParseQuestCardData } from './use-parse-quest-card-data';
import { getActiveTierDescription } from './use-parse-quest-card-data/utils';
import { getProductRequirementCmsIds, isPurchaseQuest } from './utils';

const buildChallengeDetailsPath = ({
  pathname,
  activationSource,
}: {
  pathname: string;
  activationSource: QuestActivationSource;
}) =>
  `${routes.loyaltyChallengeDetails}?previousPath=${pathname}&activationSource=${activationSource}`;

export const QuestCardWrapper = ({
  cmsQuestCard,
  quest,
  cmsQuestCardUI,
  activationSource = QuestActivationSource.UNKNOWN,
}: IQuestCardWrapperProps) => {
  const questRequirementCmsIds = useMemo(() => getProductRequirementCmsIds(quest), [quest]);
  const [
    cmsQuestDetailQuery,
    { loadingCmsQuestDetail, cmsQuestDetail, refetch: refetchCmsQuestDetail },
  ] = useCmsQuestDetailLazyQuery({ cmsId: quest.cmsId, questRequirementCmsIds });

  const {
    image,
    imageDescription,
    title,
    description,
    buttonText,
    linkText,
  } = useParseQuestCardData(quest, cmsQuestCard, cmsQuestCardUI, cmsQuestDetail);
  const { navigate } = useRouter();
  const { loyaltyUser } = useLoyaltyUser();

  const { pathname } = useRoute<{ pathname: string }>();
  const loadingQuestDetail = loadingCmsQuestDetail;

  const [activateQuest, { loading: isActivating }] = useActivateQuest({ questId: quest.id });
  const isPurchase = useMemo(() => !!(cmsQuestDetail && isPurchaseQuest(cmsQuestDetail)), [
    cmsQuestDetail,
  ]);

  const logQuestActivationRequested = useCallback(() => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_ACTIVATION_REQUESTED,
        quest,
        cmsQuestCard,
        attributes: {
          activationSource,
          loyaltyId: loyaltyUser?.id,
        },
      },
      { skipLoggingToBraze: true }
    );
  }, [activationSource, cmsQuestCard, loyaltyUser?.id, quest]);

  const callToActionCard = useCallback(async () => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_DETAILS_VIEW,
        quest,
        cmsQuestCard,
      },
      { skipLoggingToBraze: true }
    );
    if (isPurchase) {
      if (canActivateQuest(quest.status)) {
        try {
          logQuestActivationRequested();
          await activateQuest();
        } catch {
          return;
        }
      }
      navigate(routes.menu);
      return;
    }
    navigate(buildChallengeDetailsPath({ pathname, activationSource }));
  }, [
    activateQuest,
    activationSource,
    cmsQuestCard,
    isPurchase,
    logQuestActivationRequested,
    navigate,
    pathname,
    quest,
  ]);

  const onPressCardHandler = useCallback(() => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_DETAILS_VIEW,
        quest,
        cmsQuestCard,
      },
      { skipLoggingToBraze: true }
    );
    navigate(buildChallengeDetailsPath({ pathname, activationSource }));
    cmsQuestDetailQuery();
  }, [activationSource, cmsQuestCard, cmsQuestDetailQuery, navigate, pathname, quest]);

  const onShowTermsPressHandler = useCallback(() => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_DETAILS_VIEW,
        quest,
        cmsQuestCard,
      },
      { skipLoggingToBraze: true }
    );
    navigate(`${routes.questTerms}?cmsId=${cmsQuestCard._id}`);
  }, [cmsQuestCard, navigate, quest]);

  useEffect(() => {
    if (!loadingQuestDetail && pathname.includes(routes.loyaltyActivateQuest) && quest.id) {
      // If quest exists open the drawer and activate challenge
      callToActionCard();
    }
  }, [callToActionCard, loadingQuestDetail, pathname, quest.id]);

  useEffect(() => {
    if (!cmsQuestDetail && !loadingCmsQuestDetail) {
      refetchCmsQuestDetail();
    }
  }, [cmsQuestDetail, loadingCmsQuestDetail, refetchCmsQuestDetail]);

  return (
    <>
      {!loadingCmsQuestDetail && (
        <Box style={styles.cardWrapper}>
          <QuestCard
            image={image}
            altText={imageDescription}
            title={title}
            description={description}
            howToRedeem={getActiveTierDescription(quest, cmsQuestDetail)}
            callToAction={callToActionCard}
            callToActionText={buttonText}
            linkText={linkText}
            // Purchase quests have no card-level navigation; omit the handler so
            // the card stays non-interactive (no pointer cursor on web).
            onPressCard={isPurchase ? undefined : onPressCardHandler}
            onShowTermsPress={onShowTermsPressHandler}
            isQuestCompleted={isQuestCompleted(quest.status)}
            isActivating={isActivating}
            steps={quest?.steps}
            status={quest?.status}
            endDate={quest?.endDate ?? ''}
          />
        </Box>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  cardWrapper: { alignItems: 'center', marginBottom: spacing.$1_5 },
});
